<template>
    <Head :title="t('profile.collaboration.page.headTitle')" />
    <BaseLayout gap="lg" content-class="text-ink">
        <div class="flex flex-col gap-10">
            <div class="flex flex-col gap-3">
                <BasePageHeader :title="profileHeading" subtitle-below>
                    <template #subtitle>
                        <div v-if="is_self" class="flex flex-col items-start gap-2 md:flex-row md:items-baseline md:justify-between md:gap-4">
                            <span>{{ t("profile.collaboration.sharing.ownerSubtitle") }}{{ ` ${sharingDisclosure}` }}</span>
                            <BaseButton
                                theme="tertiary"
                                size="sm"
                                class="shrink-0 whitespace-nowrap px-0"
                                :class="{ 'animate-shadow-pulse': isFirstVisit }"
                                @click="isArcadeOpen = true">
                                {{ t("profile.collaboration.page.howSharingWorks") }}
                            </BaseButton>
                        </div>
                        <template v-else>
                            {{ viewerIntro }}
                            <template v-if="canComparePerspectives">{{ t("profile.collaboration.page.comparePerspectives") }}</template>
                        </template>
                        <!-- eslint-disable @intlify/vue-i18n/no-raw-text -- punctuation: the middle-dot separator between the sharing level and the last-updated date -->
                        <span v-if="profileUpdated" class="details" data-testid="profile-metadata">
                            <span v-if="is_self" data-testid="profile-access-text"
                                >{{ t("profile.collaboration.sharing.sharingLevel", { level: shareButtonText }) }}&nbsp;&middot;&nbsp;</span
                            >{{ t("profile.collaboration.page.lastUpdated", { date: profileUpdated }) }}
                        </span>
                        <!-- eslint-enable @intlify/vue-i18n/no-raw-text -- end of the separator exemption -->
                    </template>
                    <template v-if="isEditable" #actions>
                        <div class="flex gap-2">
                            <template v-if="currentAccessLevel !== ACCESS.PRIVATE">
                                <LoadingSpinner
                                    v-if="isGeneratingLink"
                                    class="self-start size-7 text-ink-muted"
                                    :aria-label="t('profile.collaboration.page.generatingShareLink')" />
                                <BaseTooltip v-else side="bottom" :side-offset="4">
                                    <template #trigger>
                                        <BaseButton
                                            theme="neutral"
                                            size="sm"
                                            icon-only
                                            :class="['self-start', tonalThemeEnabled ? 'size-8' : 'size-7']"
                                            :aria-label="isCopied ? t('profile.collaboration.page.linkCopied') : t('profile.collaboration.page.copyShareLink')"
                                            @click="handleCopyLinkWithToast">
                                            <i class="bi text-lg/none" :class="isCopied ? 'bi-check-lg text-notification-success-text' : 'bi-link-45deg'" aria-hidden="true" />
                                        </BaseButton>
                                    </template>
                                    <template #content>{{ t("profile.collaboration.page.copyLink") }}</template>
                                </BaseTooltip>
                            </template>
                            <BaseButton
                                :theme="tonalThemeEnabled ? 'neutral' : 'secondary'"
                                size="sm"
                                class="self-start flex items-center gap-2"
                                :class="{ 'animate-shadow-pulse': currentAccessLevel !== ACCESS.PRIVATE }"
                                @click="isShareProfileOpen = !isShareProfileOpen">
                                <i class="bi text-lg/none" :class="[currentAccessLevel === ACCESS.PRIVATE ? 'bi-eye-slash' : 'bi-eye']" aria-hidden="true"></i>
                                {{ shareButtonText }}
                            </BaseButton>
                        </div>
                    </template>
                </BasePageHeader>
                <StatusChip v-if="!is_self && isHrisInactive" state="neutral" size="xs" class="self-start">{{ t("profile.collaboration.page.inactive") }}</StatusChip>
                <Transition name="fade">
                    <BaseButton
                        v-if="!share_back_enabled && !is_self && connections_enabled && hasPublicProfileLinked && connectionId"
                        theme="primary"
                        class="self-start animate-shadow-pulse"
                        @click="navigateToConnection">
                        {{ t("profile.collaboration.page.viewLinkedPerson") }}
                    </BaseButton>
                </Transition>
                <ShareProfileDialog
                    v-if="existing_public_profile?.id && isEditable"
                    v-model:open="isShareProfileOpen"
                    :existing-public-profile="existing_public_profile"
                    :org-has-ms-teams="org_has_ms_teams"
                    :org-has-slack="org_has_slack"
                    :available-access-levels="availableAccessLevels" />
            </div>
            <div
                v-if="share_back_enabled && profile_creation"
                :class="{ 'order-last': is_self, 'sticky bottom-[calc(var(--mobile-nav-total-height,0px)+1rem)] z-20 md:bottom-4': isShareBackReady }">
                <ProfileShareBack
                    :key="`${profile_creation.user_basic.uuid}:${target_user_uuid}`"
                    :creation="profile_creation"
                    :profile="is_self ? (existing_public_profile ?? null) : (viewer_public_profile ?? null)"
                    :target="!is_self && target_user_uuid ? { id: target_user_uuid, name: profile_identity.name } : undefined"
                    :pending-target="share_back_target"
                    :is-self="is_self"
                    :linked="hasPublicProfileLinked"
                    :linked-by-target="is_viewer_linked_by_target"
                    :preview="preview_share_back"
                    :profile-ready="!isProfileTopicsPending && !profileTopicsErrorMessage"
                    :available-access-levels="available_access_levels"
                    :org-has-ms-teams="org_has_ms_teams"
                    :org-has-slack="org_has_slack"
                    @finished="finishShareBack" />
            </div>
            <div v-if="show_identity_card" class="rounded-2xl bg-surface-sunken" data-testid="basic-info-card">
                <div class="flex flex-col gap-4">
                    <BasicInfoCard
                        :basic-info="basic_info"
                        :identity="profile_identity"
                        :editable="isEditable"
                        :hris-data="hris_data ?? null"
                        :manager="org_tree?.manager ?? null"
                        :direct-reports="org_tree?.direct_reports ?? []"
                        :after-save="refreshAfterBasicInfoSave" />
                </div>
            </div>
            <BaseNotification v-if="isProfileUnderReview && is_self" theme="info" :text="t('profile.collaboration.page.underReview')" hide-dismiss />
            <PersonTeams
                v-if="person_teams_url"
                :key="person_teams_url"
                :source="TEAM_INTERACTION_SOURCE.COMBINED_PROFILE"
                :url="person_teams_url"
                :team-url-template="team_url_template" />
            <div :id="LINKED_PROFILES_ANCHOR_ID">
                <template v-if="show_identity_card">
                    <div class="inline-flex max-md:flex-col items-baseline gap-x-4">
                        <h2 class="display-serif">{{ t("profile.collaboration.page.howYouWorkBest") }}</h2>
                        <p class="scaffold-body-small text-ink-muted">{{ t("profile.collaboration.page.howYouWorkBestDescription") }}</p>
                    </div>
                    <HorizontalDivider class="border-t-2" />
                </template>

                <div class="rounded-lgr border border-border-strong bg-surface-raised p-4 md:p-10 shadow-sm flex flex-col gap-4">
                    <BaseTabs v-model="activeProfileTab" :tab-ids="profileTabIds" :labels="profileTabLabels" hide-single-tab-list class="gap-4" :unmount-on-hide="false">
                        <template #content-working>
                            <ProfileTopicSection
                                v-if="is_self || workingWithMeEntries.length > 0 || isProfileTopicsPending"
                                :is-loading="isProfileTopicsPending"
                                :error="profileTopicsErrorMessage"
                                :has-profile="hasExistingPublicProfile"
                                :entries="workingWithMeEntries"
                                :empty-profile-state="workingEmptyProfileState"
                                :empty-entries-state="workingEmptyEntriesState"
                                :editable="isEditable"
                                :can-recreate="is_self"
                                :perspective-letters="perspectiveLetters"
                                :is-manager="isManager"
                                :has-perspective-report="hasPerspectiveReport"
                                :topic-name="PROFILE_TOPIC_NAMES.WORKING_WITH_ME"
                                :free-text-enabled="freeTextEnabled"
                                @saved="handleSubtopicSaved"
                                @recreate="handleProfileRegenerated">
                                <template #sidebar>
                                    <LinkedProfilesCard
                                        :linked-count="linked_profiles_count"
                                        :can-add-colleagues="connections_enabled"
                                        @add-from-organization="showOrganizationDirectory = true" />
                                </template>
                            </ProfileTopicSection>
                        </template>
                        <template #content-team>
                            <ProfileTopicSection
                                v-if="is_self || teamWorkEntries.length > 0 || isProfileTopicsPending"
                                :is-loading="isProfileTopicsPending"
                                :error="profileTopicsErrorMessage"
                                :has-profile="hasExistingPublicProfile"
                                :entries="teamWorkEntries"
                                :empty-profile-state="teamEmptyProfileState"
                                :empty-entries-state="teamEmptyEntriesState"
                                :perspective-letters="perspectiveLetters"
                                :editable="isEditable"
                                :can-recreate="is_self"
                                :is-manager="isManager"
                                :has-perspective-report="hasPerspectiveReport"
                                :topic-name="PROFILE_TOPIC_NAMES.TEAM_WORK"
                                :free-text-enabled="freeTextEnabled"
                                @saved="handleSubtopicSaved"
                                @recreate="handleProfileRegenerated">
                                <template #sidebar>
                                    <LinkedProfilesCard
                                        :linked-count="linked_profiles_count"
                                        :can-add-colleagues="connections_enabled"
                                        @add-from-organization="showOrganizationDirectory = true" />
                                </template>
                            </ProfileTopicSection>
                        </template>
                    </BaseTabs>
                </div>
            </div>
        </div>
    </BaseLayout>
    <BaseToast v-model:open="isCopyLinkSuccessToastOpen" :duration="TOAST_DURATION_MS" :description="t('profile.collaboration.page.linkCopiedToast')" />
    <BaseToast v-model:open="isShareLinkErrorToastOpen" :duration="TOAST_DURATION_MS" theme="error" :description="shareLinkError ?? ''" />
    <BaseToast v-model:open="isShareBackDisabledToastOpen" theme="error" :description="t('profile.collaboration.page.shareBackDisabled')" :truncate-length="200" />
    <BaseToast
        v-model:open="isAutoLinkSuccessToastOpen"
        :duration="TOAST_DURATION_MS"
        :description="autoLinkSuccessMessage"
        :action-text="t('profile.collaboration.page.viewPerson')"
        @action="handleViewAutoLinkedConnection" />
    <BaseToast
        v-model:open="isAutoLinkErrorToastOpen"
        :duration="TOAST_DURATION_MS"
        theme="error"
        :description="t('profile.collaboration.page.autoLinkFailed')"
        :action-text="t('common.tryAgain')"
        @action="autoLinkOnMount" />
    <BaseDialog
        v-model:open="isMatchConfirmOpen"
        :title="t('profile.collaboration.acceptShared.confirmTitle', { name: matchConfirmFirstName })"
        layout="custom"
        width="default"
        mobile-height="expanded"
        show-close>
        <AmbiguousMatchConfirm
            :candidates="matchCandidates"
            :first-name="matchConfirmFirstName"
            :error="matchResolutionError"
            :busy="isEnsuringLink"
            @select="resolveMatch"
            @create-new="resolveMatch(null)" />
    </BaseDialog>
    <DefaultSharingDialog v-model:open="isSharingIntroOpen" :access="currentAccessLevel" :available-access-levels="availableAccessLevels" />
    <DemoModal v-model:open="isArcadeOpen" :demo-url="SHARING_DEMO_URL" :header-text="t('profile.collaboration.page.howSharingWorksDemo')" />
    <OrganizationProfileDirectory
        v-if="showOrganizationDirectory"
        v-model:open="showOrganizationDirectory"
        :has-collaboration-profile="has_collaboration_profile"
        :create-profile-url="create_profile_url"
        :invite-url="collaboration_profile_invite_url"
        source="collaboration_profile"
        @added="directoryChanged = true"
        @removed="directoryChanged = true" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CollabProfileEnsureLinkResult, CollabProfileLinkRequest, ConnectionMatchCandidate, ProfileShareAttribution } from "~vue/api/generated/collab-profiles";
import { dismissNotificationLocallyKey, type HrisData, type OrgTree, panesWithNotificationKey } from "~vue/appTypes";
import AmbiguousMatchConfirm from "~vue/CollaborationProfile/AmbiguousMatchConfirm.vue";
import type { ShareBackTarget } from "~vue/CollaborationProfile/composables/useProfileShareBack";
import { useProfileShareLink } from "~vue/CollaborationProfile/composables/useProfileShareLink";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import DefaultSharingDialog from "~vue/CollaborationProfile/DefaultSharingDialog.vue";
import LinkedProfilesCard from "~vue/CollaborationProfile/LinkedProfilesCard.vue";
import { logProfileShared } from "~vue/CollaborationProfile/profileShareAttribution";
import ProfileShareBack from "~vue/CollaborationProfile/ProfileShareBack.vue";
import ShareProfileDialog from "~vue/CollaborationProfile/ShareProfileDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BasicInfoCard from "~vue/components/BasicInfoCard.vue";
import DemoModal from "~vue/components/DemoModal.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { MainMenuPane } from "~vue/components/navigation/menuPanes";
import type { ProfileEntry } from "~vue/components/ProfileTopicSection.vue";
import ProfileTopicSection from "~vue/components/ProfileTopicSection.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import OrganizationProfileDirectory from "~vue/Connections/OrganizationProfileDirectory.vue";
import PersonTeams from "~vue/Connections/PersonTeams.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import type { ExistingPublicProfile, ProfileBasicInfo, ProfileCreationProps, ProfileIdentity } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS } from "~vue/utils/constants";
import { formatDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { toProfileEntry } from "~vue/utils/profileEntries";
import { PROFILE_TOPIC_NAMES, type ProfileTopicName } from "~vue/utils/profileTopicNames";
import { capitalizeName, getFirstName } from "~vue/utils/stringUtils";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { canComparePerspectives, sharingDisclosure, accessButtonLabel } = useProfileSharingCopy();

const SHARING_DEMO_URL = "https://demo.arcade.software/NoUVnEW6MTDCEurJr1rm?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true";

// Deep-link target for the coaching journey's "Link Collaboration Profiles" step, built in
// accounts.services.profile_completion_service.
const LINKED_PROFILES_ANCHOR_ID = "linked-profiles";
const LINKED_PROFILES_HASH = `#${LINKED_PROFILES_ANCHOR_ID}`;

const props = withDefaults(
    defineProps<{
        is_self?: boolean;
        user_name?: string | null;
        target_user_uuid?: string | null;
        person_teams_url?: string | null;
        team_url_template: string;
        // Set only on the first render after arriving through a shared link.
        share_attribution?: ProfileShareAttribution | null;
        basic_info: ProfileBasicInfo;
        profile_identity: ProfileIdentity;
        show_identity_card?: boolean;
        available_access_levels?: string[];
        show_sharing_intro?: boolean;
        existing_public_profile?: ExistingPublicProfile | null;
        hris_data?: HrisData | null;
        org_tree?: OrgTree | null;
        org_has_ms_teams: boolean;
        org_has_slack: boolean;
        viewer_connection_id: string | null;
        is_collab_profile_linked: boolean;
        connections_enabled?: boolean;
        linked_profiles_count?: number;
        has_collaboration_profile?: boolean;
        create_profile_url?: string;
        collaboration_profile_invite_url?: string;
        // this one encompasses both the assessment and report flags
        user_perspective_report_enabled: boolean;
        share_back_enabled?: boolean;
        is_viewer_linked_by_target?: boolean;
        share_back_target?: ShareBackTarget | null;
        preview_share_back?: boolean;
        profile_creation?: ProfileCreationProps | null;
        viewer_public_profile?: ExistingPublicProfile | null;
    }>(),
    {
        is_self: true,
        show_identity_card: true,
        user_name: null,
        target_user_uuid: null,
        person_teams_url: null,
        share_attribution: null,
        available_access_levels: () => ["private", "organization"],
        show_sharing_intro: false,
        existing_public_profile: null,
        hris_data: null,
        org_tree: null,
        org_has_ms_teams: false,
        org_has_slack: false,
        is_collab_profile_linked: false,
        connections_enabled: false,
        linked_profiles_count: 0,
        has_collaboration_profile: false,
        create_profile_url: "",
        collaboration_profile_invite_url: "",
        share_back_enabled: false,
        is_viewer_linked_by_target: false,
        share_back_target: null,
        preview_share_back: false,
        profile_creation: null,
        viewer_public_profile: null,
    },
);
const panesWithNotifications = inject(panesWithNotificationKey, ref([]));
const dismissNotificationLocally = inject(dismissNotificationLocallyKey, () => {});
watch(
    panesWithNotifications,
    async (newVal) => {
        if (newVal?.includes(MainMenuPane.COLLABORATION_PROFILE) && props.is_self) {
            dismissNotificationLocally(MainMenuPane.COLLABORATION_PROFILE);
            await useFetch("/v1/navigation/notifications/dismiss").post({ pane: "collaboration_profile" }).json();
        }
    },
    { immediate: true },
);

enum ProfileTabId {
    WorkingWithMe = "working",
    TeamWork = "team",
}

const activeProfileTab = ref<ProfileTabId>(ProfileTabId.WorkingWithMe);
const profileTabLabels = computed(() => ({
    [ProfileTabId.WorkingWithMe]: t("profile.collaboration.page.workingWithMeTab"),
    [ProfileTabId.TeamWork]: t("profile.collaboration.page.teamWorkTab"),
}));
const profileTabIds = computed(() => {
    const ids = [ProfileTabId.WorkingWithMe];
    if (isManager.value) {
        ids.push(ProfileTabId.TeamWork);
    }
    return ids;
});

interface SubtopicCurrent {
    content: string;
    access: string;
}

interface PropertySubtopic {
    id: string;
    title: string;
    placeholder: string;
    order: number;
    current?: SubtopicCurrent;
}

interface PropertyTopic {
    id: string;
    topic: string;
    placeholder: string;
    managers_only?: boolean;
    subtopics: PropertySubtopic[];
}

interface PropertyTopicsResponse {
    topics: PropertyTopic[];
}

const availableAccessLevels = computed(() => new Set(props.available_access_levels ?? []));
const hasExistingPublicProfile = computed(() => Boolean(props.existing_public_profile));
const isHrisInactive = computed(() => props.existing_public_profile?.is_inactive ?? false);
const isEditable = computed(() => hasExistingPublicProfile.value && props.is_self);
const isProfileUnderReview = computed(() => props.existing_public_profile?.is_flagged ?? false);
const perspectiveLetters = computed<string | null>(() => props.existing_public_profile?.perspective_letters ?? null);
const hasPerspectiveReport = computed(() => Boolean(perspectiveLetters.value));

const displayName = computed(() => props.profile_identity.name || props.basic_info.full_name || t("components.profile.public.unknown"));

const profileHeading = computed(() => {
    if (props.is_self) {
        return t("profile.collaboration.page.ownHeading");
    }
    if (displayName.value) {
        return t("profile.collaboration.page.namedHeading", { name: displayName.value });
    }
    return t("profile.collaboration.page.colleagueHeading");
});

const viewerIntro = computed(() =>
    displayName.value ? t("profile.collaboration.page.viewerIntro", { name: displayName.value }) : t("profile.collaboration.page.viewerIntroUnnamed"),
);

const profileTopicsUrl = computed(() => (props.is_self || !props.target_user_uuid ? "/api/accounts/profile/topics/" : `/api/accounts/profile/topics/${props.target_user_uuid}/`));

const {
    data: profileTopics,
    isFetching: isProfileTopicsLoading,
    error: profileTopicsError,
    execute: fetchProfileTopics,
} = useFetch(profileTopicsUrl, { immediate: false, refetch: true }).json<PropertyTopicsResponse>();

const profileTopicsErrorMessage = computed(() => {
    if (!profileTopicsError.value) {
        return null;
    }
    if (typeof profileTopicsError.value === "string") {
        return profileTopicsError.value;
    }
    return profileTopicsError.value.message || t("profile.collaboration.page.loadFailed");
});

const shouldFetchProfileTopics = computed(() => hasExistingPublicProfile.value);

watch(
    shouldFetchProfileTopics,
    async (shouldFetch) => {
        if (shouldFetch) {
            await fetchProfileTopics();
        }
    },
    { immediate: true },
);

const hasProfileTopicsData = computed(() => profileTopics.value !== null);
const isProfileTopicsPending = computed(() => isProfileTopicsLoading.value && !hasProfileTopicsData.value);

const localSubtopicUpdates = reactive<Record<string, string>>({});

const freeTextEnabled = computed(() => !hasPerspectiveReport.value && !props.user_perspective_report_enabled);

const profileTopicMap = computed(() => {
    const topics = profileTopics.value?.topics ?? [];
    return new Map(topics.map((topic) => [topic.topic, topic]));
});

function buildProfileEntries(topicName: ProfileTopicName): ProfileEntry[] {
    const topic = profileTopicMap.value.get(topicName);
    if (!topic) {
        return [];
    }

    return topic.subtopics.map((subtopic) =>
        toProfileEntry({
            id: subtopic.id,
            title: subtopic.title,
            content: localSubtopicUpdates[subtopic.id] ?? subtopic.current?.content ?? "",
            placeholder: subtopic.placeholder,
        }),
    );
}

async function refreshAfterBasicInfoSave(): Promise<void> {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_BASIC_INFO_SAVED);
    fetchProfileTopics();
    await new Promise<void>((resolve) => router.reload({ only: ["profile_identity", "existing_public_profile", "basic_info", "user_name"], onFinish: () => resolve() }));
}

function handleSubtopicSaved(id: string, content: string): void {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SUBTOPIC_EDITED, {
        subtopic_id: id,
        content_length: content.length,
    });
    localSubtopicUpdates[id] = content;
    fetchProfileTopics(); // background sync, no await
}

const workingWithMeEntries = computed(() => buildProfileEntries(PROFILE_TOPIC_NAMES.WORKING_WITH_ME));
const teamWorkEntries = computed(() => buildProfileEntries(PROFILE_TOPIC_NAMES.TEAM_WORK));

const PROFILE_MANAGE_HREF = "/profile/create";

const isManager = computed(() => props.existing_public_profile?.is_manager ?? false);

const workingEmptyProfileState = computed(() => ({
    title: t("profile.collaboration.emptyState.noProfile.title"),
    body: t("profile.collaboration.emptyState.noProfile.workingBody"),
    icon: "bi-person-lines-fill",
    ctaText: t("profile.collaboration.emptyState.noProfile.cta"),
    ctaHref: PROFILE_MANAGE_HREF,
}));

const workingEmptyEntriesState = computed(() => ({
    title: t("profile.collaboration.emptyState.noEntries.title"),
    body: t("profile.collaboration.emptyState.noEntries.workingBody"),
    icon: "bi-chat-left-text",
    ctaText: t("profile.collaboration.emptyState.noEntries.workingCta"),
    // No ctaHref → renders as a button that emits @recreate (inline regenerate).
}));

const teamEmptyProfileState = computed(() => ({
    title: t("profile.collaboration.emptyState.noProfile.title"),
    body: t("profile.collaboration.emptyState.noProfile.teamBody"),
    icon: "bi-people",
    ctaText: t("profile.collaboration.emptyState.noProfile.cta"),
    ctaHref: PROFILE_MANAGE_HREF,
}));

const teamEmptyEntriesState = computed(() => ({
    title: t("profile.collaboration.emptyState.noEntries.title"),
    body: t("profile.collaboration.emptyState.noEntries.teamBody"),
    icon: "bi-kanban",
    ctaText: t("profile.collaboration.emptyState.noEntries.teamCta"),
    // No ctaHref → renders as a button that emits @recreate (inline regenerate).
}));

const isFirstVisit = computed(() => panesWithNotifications.value?.includes(MainMenuPane.COLLABORATION_PROFILE));
const isArcadeOpen = ref(false);
const isShareProfileOpen = ref<boolean>(false);

const showOrganizationDirectory = ref(false);
const directoryChanged = ref(false);
watch(showOrganizationDirectory, (isOpen) => {
    if (!isOpen && directoryChanged.value) {
        directoryChanged.value = false;
        // Stay on this page and let the linked count update in place; the count links to the filtered My People view.
        router.reload({ only: ["linked_profiles_count"] });
    }
});

const connectionId = computed(() => props.viewer_connection_id ?? null);
const hasPublicProfileLinked = computed(() => props.is_collab_profile_linked);

const navigateToConnection = () => {
    router.visit(`/people/${connectionId.value}/`);
};

const handleProfileRegenerated = async (): Promise<void> => {
    Object.keys(localSubtopicUpdates).forEach((id) => delete localSubtopicUpdates[id]);
    await fetchProfileTopics();
    router.reload({ only: ["existing_public_profile"] });
};

const currentAccessLevel = computed<ACCESS>(() => (props.existing_public_profile?.access as ACCESS | undefined) ?? ACCESS.PRIVATE);

const isShareBackReady = computed(() => props.is_self && !!props.existing_public_profile && !!props.share_back_target);
const isSharingIntroOpen = ref(props.show_sharing_intro && !props.share_back_target);
const finishShareBack = () => {
    const url = new URL(globalThis.location.href);
    url.searchParams.delete("share_back");
    router.replace({
        url: `${url.pathname}${url.search}${url.hash}`,
        props: (current) => ({ ...current, share_back_target: null }),
        preserveScroll: true,
        preserveState: true,
    });
};
const isShareBackDisabledToastOpen = ref(false);
watch(
    () => props.share_back_enabled,
    (enabled, previouslyEnabled) => {
        if (previouslyEnabled && !enabled) {
            isShareBackDisabledToastOpen.value = true;
        }
    },
);

const shareButtonText = computed(() => accessButtonLabel(currentAccessLevel.value));

const profileUpdated = computed(() => {
    if (props.existing_public_profile?.updated_at) {
        return formatDate(props.existing_public_profile.updated_at, "datetime");
    }
    return null;
});

const { isCopied, handleCopyLink, isGeneratingLink, shareLinkError } = useProfileShareLink({
    profileId: computed(() => props.existing_public_profile?.id),
    shareSource: "profile_page",
});

const isCopyLinkSuccessToastOpen = ref(false);
const handleCopyLinkWithToast = async () => {
    const access = currentAccessLevel.value;
    const copied = await handleCopyLink(access, () => {
        isCopyLinkSuccessToastOpen.value = true;
    });
    if (copied) {
        void logProfileShared({ shareSource: "profile_page", target: "link", shareKind: "own_profile", access });
    }
};

const TOAST_DURATION_MS = 6000;

const isShareLinkErrorToastOpen = ref(false);
watch(shareLinkError, (err) => {
    if (err) {
        isShareLinkErrorToastOpen.value = true;
    }
});

const isAutoLinkSuccessToastOpen = ref<boolean>(false);

const ensureLinkUrl = computed(() => (props.target_user_uuid ? `/v1/collaboration-profiles/${props.target_user_uuid}/ensure-link` : ""));
// Asks for name matches first; then the viewer's answer: a picked card, or {} for a fresh one.
const matchResolution = ref<Pick<CollabProfileLinkRequest, "connection_id" | "match_by_name">>({ match_by_name: true });
const ensureLinkPayload = computed<CollabProfileLinkRequest>(() => ({ share_attribution: props.share_attribution, ...matchResolution.value }));
const {
    data: ensureLinkData,
    error: ensureLinkError,
    isFetching: isEnsuringLink,
    execute: executeEnsureLink,
} = useFetch(ensureLinkUrl, { immediate: false }).post(ensureLinkPayload).json<CollabProfileEnsureLinkResult>();

const matchCandidates = ref<ConnectionMatchCandidate[]>([]);
const matchResolutionError = ref<string | null>(null);
const isMatchConfirmOpen = computed({
    get: () => matchCandidates.value.length > 0,
    set: (open: boolean) => {
        if (!open) {
            matchCandidates.value = [];
        }
    },
});
const matchConfirmFirstName = computed(() => capitalizeName(getFirstName(displayName.value)));

const autoLinkSuccessMessage = computed(() => {
    const action = ensureLinkData.value?.action;
    if (!action || action === "noop") {
        return "";
    }
    const name = displayName.value;
    return action === "created" ? t("profile.collaboration.page.autoLinkCreated", { name }) : t("profile.collaboration.page.autoLinkLinked", { name });
});

const handleViewAutoLinkedConnection = (): void => {
    const id = ensureLinkData.value?.connection_id;
    if (id) {
        router.visit(`/people/${id}/`);
    }
};

const isAutoLinkErrorToastOpen = ref<boolean>(false);

const autoLinkOnMount = async (): Promise<void> => {
    if (!props.connections_enabled || props.is_self || !props.target_user_uuid || (props.is_collab_profile_linked && props.viewer_connection_id)) {
        return;
    }

    await executeEnsureLink();
    if (ensureLinkError.value) {
        if (isMatchConfirmOpen.value) {
            matchResolutionError.value = t("profile.collaboration.acceptShared.resolveError");
        } else {
            isAutoLinkErrorToastOpen.value = true;
        }
        return;
    }
    const result = ensureLinkData.value;
    matchCandidates.value = result?.match_candidates ?? [];
    if (!result?.action || result.action === "noop") {
        return;
    }
    await new Promise<void>((resolve) =>
        router.reload({
            only: ["is_collab_profile_linked", "viewer_connection_id"],
            onFinish: () => resolve(),
        }),
    );
    isAutoLinkSuccessToastOpen.value = true;
};

const resolveMatch = async (connectionId: string | null): Promise<void> => {
    if (isEnsuringLink.value) {
        return;
    }
    matchResolution.value = connectionId ? { connection_id: connectionId } : {};
    matchResolutionError.value = null;
    await autoLinkOnMount();
};

onMounted(() => {
    void autoLinkOnMount();
    if (props.show_sharing_intro) {
        // Inertia restores cached props on back/forward, so drop the flag from history state too.
        router.replace({
            props: (currentProps) => ({ ...currentProps, show_sharing_intro: false }),
            preserveState: true,
            preserveScroll: true,
        });
    }
});

if (props.is_self && globalThis.location.hash === LINKED_PROFILES_HASH) {
    watch(
        isProfileTopicsPending,
        async () => {
            await nextTick();
            document.getElementById(LINKED_PROFILES_ANCHOR_ID)?.scrollIntoView({ behavior: "smooth" });
        },
        { once: true },
    );
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
